// Turns an AI answer into displayable blocks.
//
// The model is not constrained to plain prose - it writes the markdown it
// naturally reaches for: "- " bullets, "1." lists, "**bold**", the odd
// heading. Rendered as one string that arrives as an unbroken wall of text
// with literal asterisks in it. This parses the small subset that actually
// shows up, and nothing more: no HTML and no links, so there is no injection
// surface and nothing to keep in step with a markdown spec.

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*(\d+)[.)]\s+(.*)$/;
const HEADING = /^\s*(#{1,4})\s+(.*)$/;
// A markdown table row: starts and ends with a pipe. Asked to compare things,
// a model reaches for a table without being told to - and with no case for it
// here, every row fell through to the paragraph branch, which joins lines with
// a SPACE. A nine-column comparison came back as one line reading
// "| Parameter | Siderise EW | ... | |----|----| | Material | ...": the data
// intact, the structure destroyed, and the reader blaming the model.
const TABLE_ROW = /^\s*\|(.+)\|\s*$/;
// The |---|:--:|---| line under the header. Alignment colons are accepted and
// then ignored; it carries no data either way.
const TABLE_DIVIDER = /^\s*\|[\s:|-]+\|\s*$/;

function tableCells(line) {
  const inner = line.match(TABLE_ROW)?.[1] ?? "";
  return inner.split("|").map((cell) => stripInlineMarks(cell.trim()));
}
// "Heading:" on a line of its own - how models most often label a section
// when they haven't reached for a markdown heading. Deliberately narrow: a
// full sentence ending in a colon ("Here are the drawings that changed on
// Level 1:") is an introduction, not a heading, so a label has to be a short
// phrase with no sentence punctuation in it.
const LABEL = /^\s*([A-Z][^.!?,;]{0,44}):\s*$/;
const LABEL_MAX_WORDS = 5;

function stripInlineMarks(text) {
  return String(text ?? '').replace(/\*\*/g, '').replace(/(^|\s)\*(?=\S)/g, '$1').trim();
}

// Splits a line into plain and bold runs, plus [1]-style citation markers, so
// the renderer can style them without interpreting anything else.
export function parseInline(text) {
  const parts = [];
  const pattern = /\*\*(.+?)\*\*|`([^`]+)`|(\[\d+(?:\s*,\s*\d+)*\])/g;
  let last = 0;
  let m;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    if (m[1] !== undefined) parts.push({ type: 'bold', value: m[1] });
    else if (m[2] !== undefined) parts.push({ type: 'code', value: m[2] });
    else parts.push({ type: 'cite', value: m[3] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return parts.length ? parts : [{ type: 'text', value: text }];
}

export function formatAnswer(answer = '') {
  const lines = String(answer ?? '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let paragraph = [];
  let list = null;
  let table = null;

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: 'p', text: paragraph.join(' ').trim() });
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    blocks.push(list);
    list = null;
  };
  // Every block carries `text`, including this one. ChatScreen.js (the Expo
  // app) shares this parser and renders anything it does not recognise through
  // block.text - so a table block without it would render as nothing at all,
  // and a web-only change would silently delete the table on mobile. The
  // fallback is tab separated, which is what pastes into a spreadsheet.
  const flushTable = () => {
    if (!table) return;
    const all = [table.header, ...table.rows].filter((row) => row.length);
    if (all.length) {
      blocks.push({
        type: 'table',
        header: table.header,
        rows: table.rows,
        text: all.map((row) => row.join('\t')).join('\n'),
      });
    }
    table = null;
  };
  const flushAll = () => { flushParagraph(); flushList(); flushTable(); };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, '');
    if (!line.trim()) { flushAll(); continue; }

    // Tables first: a divider line is also matchable as a bullet ("|---|"
    // is not, but a row whose first cell starts with "- " would be), and a
    // row's cells can contain anything. Checking here keeps the rest of the
    // parser from ever seeing part of a table.
    // The divider carries no data wherever it appears, and it also matches
    // TABLE_ROW - so it is dropped before that test, or it would become a
    // header row of dashes.
    if (TABLE_DIVIDER.test(line)) continue;
    if (TABLE_ROW.test(line)) {
      flushParagraph();
      flushList();
      const cells = tableCells(line);
      if (!table) table = { header: cells, rows: [] };
      else table.rows.push(cells);
      continue;
    }
    // Any other line ends the table.
    flushTable();

    const heading = line.match(HEADING);
    if (heading) {
      flushAll();
      blocks.push({ type: 'h', text: stripInlineMarks(heading[2]) });
      continue;
    }

    const bullet = line.match(BULLET);
    if (bullet) {
      flushParagraph();
      if (list?.type !== 'ul') { flushList(); list = { type: 'ul', items: [] }; }
      list.items.push(bullet[1].trim());
      continue;
    }

    const numbered = line.match(NUMBERED);
    if (numbered) {
      flushParagraph();
      if (list?.type !== 'ol') { flushList(); list = { type: 'ol', items: [] }; }
      list.items.push(numbered[2].trim());
      continue;
    }

    const label = line.match(LABEL);
    if (label && label[1].trim().split(/\s+/).length <= LABEL_MAX_WORDS) {
      flushAll();
      blocks.push({ type: 'h', text: stripInlineMarks(label[1]) });
      continue;
    }

    // A plain line: continues the paragraph, and ends any list before it.
    flushList();
    paragraph.push(line.trim());
  }

  flushAll();
  return blocks;
}
